/* Medical ID Shop: form-associated engraving and cart editing. No browser storage. */
(() => {
  const drafts = new Map();
  let sequence = 0;
  const integer = value => Math.max(0, Math.floor(Number(value) || 0));
  const restoredBraceletIds = new Set(['15397192499500', '15397192532268']);
  const scopedEngravingCharacters = element => {
    if (!restoredBraceletIds.has(String(element.dataset.productId))) return 30;
    const requested = integer(element.dataset.characters);
    return requested === 20 ? requested : 20;
  };
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  class MedicalPersonalizer extends HTMLElement {
    connectedCallback() {
      if (this.ready) { this.observePreview(); this.schedulePreviewFit(); this.observePrice(); this.updateExtras(); return; }
      this.raw = this.querySelector('.med-engraving-fallback textarea');
      if (!this.raw) return;
      this.ready = true;
      this.uid = `med-lines-${++sequence}`;
      this.draftKey = `${this.dataset.productId}:${this.dataset.formId}`;
      this.restoredBracelet = restoredBraceletIds.has(String(this.dataset.productId));
      this.characters = scopedEngravingCharacters(this);
      const examples = [
        ['JAMIE PARKER', 'TYPE 1 DIABETES', 'ICE 202-555-0104'],
        ['RILEY BENNETT', 'PENICILLIN ALLERGY', 'ICE 202-555-0116'],
        ['MORGAN REED', 'ASTHMA', 'ICE 202-555-0128'],
        ['CASEY TAYLOR', 'EPILEPSY', 'ICE 202-555-0135'],
        ['JORDAN ELLIS', 'PEANUT ALLERGY', 'ICE 202-555-0147'],
        ['AVERY WILSON', 'TYPE 2 DIABETES', 'ICE 202-555-0152'],
        ['CAMERON LEE', 'LATEX ALLERGY', 'ICE 202-555-0163'],
        ['QUINN HAYES', 'PACEMAKER', 'ICE 202-555-0179']
      ];
      let exampleHash = 0;
      for (const char of this.dataset.productId || '') exampleHash = (exampleHash * 31 + char.charCodeAt(0)) >>> 0;
      this.exampleLines = examples[exampleHash % examples.length];
      this.methodOptions = this.querySelector('.med-engraving-methods');
      if (this.methodOptions) this.methodOptions.hidden = false;
      const ratio = Number(this.dataset.customTagRatio);
      if (ratio > 0) this.style.setProperty('--med-tag-ratio', String(Math.max(.2, Math.min(6, ratio))));
      this.querySelectorAll('[data-med-addon]').forEach(input => {
        input.disabled = input.dataset.available !== 'true' || !/^\d+$/.test(input.dataset.variantId || '');
      });
      this.querySelector('.med-method-input[data-method="standard"]')?.removeAttribute('disabled');
      this.front = integer(this.dataset.frontLines);
      this.back = integer(this.dataset.backLines);
      this.structured = this.front > 0 || this.back > 0;
      this.sidesEnabled = this.dataset.sidesEnabled === 'true';
      this.sideLabels = {front:this.dataset.frontLabel || 'Front', back:this.dataset.backLabel || 'Back', engraving:'Engraving'};
      this.sideChoices = this.querySelector('.med-engraving-sides');
      this.mode = this.querySelector('.med-personalization-mode');
      this.builder = this.querySelector('.med-engraving-builder');
      this.confirm = this.querySelector('.med-engraving-confirm input');
      this.error = this.querySelector('.med-personalizer-error');
      this.groups = this.querySelector('.med-line-groups');
      this.preview = this.querySelector('.med-preview-content');
      this.previewControls = node('div', 'med-preview-tabs');
      this.previewControls.setAttribute('aria-label', 'Engraving preview side');
      this.preview.prepend(this.previewControls);
      this.previewControls.addEventListener('click', event => {
        const button = event.target.closest('[data-preview-side]');
        if (!button) return;
        this.activePreviewSide = button.dataset.previewSide;
        this.showPreviewSide();
        this.dispatchEvent(new CustomEvent('med:engraving-update', {bubbles:true, detail:{sideChanged:true}}));
      });
      this.fields = [];
      const draft = drafts.get(this.draftKey);
      this.fontDraft = draft?.font;
      this.fontControl = this.querySelector('med-engraving-font');
      this.selectedSides = draft?.selectedSides || this.nativeSideChoice() || this.dataset.defaultSide || 'back';
      const both = this.querySelector('[data-med-side=both]');
      if (this.selectedSides === 'both' && both?.disabled) this.selectedSides = this.dataset.defaultSide || 'back';
      this.activePreviewSide = draft?.previewSide || (this.selectedSides === 'both' ? this.dataset.defaultSide : this.selectedSides);
      if (this.sidesEnabled && this.sideChoices) {
        this.sideChoices.hidden = false;
        this.querySelectorAll('[data-med-side]').forEach(input => { input.checked = input.dataset.medSide === this.selectedSides; });
        this.nativeSideGroup()?.setAttribute('hidden','');
      }
      const rawLines = this.raw.value.split('\n');
      if (this.front) this.addGroup(this.sideLabels.front, Math.max(this.front, draft?.front?.length || 0), draft?.front || [], 'front');
      if (this.back) this.addGroup(this.sideLabels.back, Math.max(this.back, draft?.back?.length || 0), draft?.back || [], 'back');
      if (!this.structured) this.addGroup('Engraving', Math.max(3, draft?.engraving?.length || rawLines.length), draft?.engraving || rawLines);
      this.querySelector('.med-capacity-note').textContent = `Up to ${this.characters} characters per line.`;
      this.querySelector('.med-engraving-fallback').hidden = true;
      this.raw.required = false;
      this.mode.hidden = false;
      this.mode.querySelectorAll('input').forEach(input => {
        input.disabled = false;
        input.checked = input.value === (draft?.mode || 'Personalized');
      });
      this.confirm.checked = draft?.checked || false;
      const method = this.querySelector(`.med-method-input[data-method="${draft?.method || 'standard'}"]`);
      if (method && !method.disabled) method.checked = true;
      for (const input of this.querySelectorAll('input[type="checkbox"][data-med-addon]')) {
        if (!input.disabled) input.checked = Boolean(draft?.extras?.includes(input.dataset.medAddon));
      }
      const savedColor = [...this.querySelectorAll('[data-med-fill-color]')].find(input => input.value === draft?.fillColor);
      if (savedColor) savedColor.checked = true;
      const wrist = this.querySelector('.med-wrist-select');
      if (wrist && [...wrist.options].some(option => option.value === draft?.wrist)) wrist.value = draft.wrist;
      this.addEventListener('input', event => {
        if (event.target.matches('.med-line-input')) {
          this.activePreviewSide = event.target.dataset.side;
          this.sync(true, true);
        }
      });
      this.addEventListener('change', event => {
        if (event.target.closest('.med-personalization-mode')) {
          this.confirm.checked = false;
          this.sync(false);
          this.syncNativeSide();
        } else if (event.target.matches('[data-med-side]')) {
          this.selectedSides = event.target.dataset.medSide;
          if (this.selectedSides !== 'both') this.activePreviewSide = this.selectedSides;
          this.sync(true, false, true);
          this.syncNativeSide();
        } else if (event.target === this.confirm || event.target.matches('[data-med-addon],.med-wrist-select,.med-method-input,[data-med-fill-color]')) this.sync(event.target.matches('.med-method-input,[data-med-fill-color],[data-med-addon="color-fill"]'));
      });
      this.observePrice();
      this.updateAccessoryTone(this.dataset.initialTone || 'silver');
      this.fontControl?.initialize?.(this.fontDraft);
      this.sync(false);
      this.observePreview();
      document.fonts?.ready?.then(() => {
        this.previewFitCache = new WeakMap();
        if (this.isConnected) this.schedulePreviewFit();
      });
    }
    disconnectedCallback() {
      this.priceScope?.removeEventListener('variant:update', this.onPriceVariant);
      this.priceScope?.removeEventListener('variant:selected', this.onPricePending);
      this.previewObserver?.disconnect();
      if (this.onPreviewResize) window.removeEventListener('resize', this.onPreviewResize);
      this.cancelPreviewFit();
    }
    nativeSideGroup() {
      const picker = [...(this.closest('.shopify-section')?.querySelectorAll('variant-picker') || [])]
        .find(item => item.dataset.productId === this.dataset.productId);
      return picker?.querySelector('[data-med-native-side]');
    }
    nativeSideChoice() {
      if (!this.sidesEnabled) return null;
      const group = this.nativeSideGroup();
      const value = group?.querySelector('input:checked')?.value || group?.querySelector('select')?.value;
      if (!value) return null;
      return /both/i.test(value) ? 'both' : /front/i.test(value) ? 'front' : /back/i.test(value) ? 'back' : null;
    }
    syncNativeSide() {
      if (!this.sidesEnabled) return;
      const group = this.nativeSideGroup();
      if (!group) return;
      const side = this.dataset.engravingMethod === 'none' ? this.dataset.defaultSide || 'back' : this.selectedSides;
      const matches = value => side === 'both' ? /both/i.test(value) : side === 'front' ? /front/i.test(value) : /back/i.test(value);
      const radio = [...group.querySelectorAll('input[type=radio]')].find(input => matches(input.value));
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change',{bubbles:true}));
      } else if (!radio) {
        const select = group.querySelector('select');
        const option = [...(select?.options || [])].find(item => matches(item.value));
        if (select && option && select.value !== option.value) {
          select.value = option.value;
          select.dispatchEvent(new Event('change',{bubbles:true}));
        }
      }
    }
    observePrice() {
      this.priceScope?.removeEventListener('variant:update', this.onPriceVariant);
      this.priceScope?.removeEventListener('variant:selected', this.onPricePending);
      this.priceScope = this.closest('.shopify-section, dialog');
      this.onPriceVariant ||= event => {
        const productId = event.detail?.data?.newProduct?.id || event.detail?.data?.productId || event.target.dataset?.productId;
        if (String(productId) !== this.dataset.productId) return;
        const variant = event.detail?.resource;
        this.variantPending = false;
        this.priceUnavailable = !variant;
        if (variant && Number.isFinite(Number(variant.price))) this.dataset.basePrice = String(variant.price);
        // Horizon replaces both the main and sticky price during this event.
        // Apply extras after those native handlers finish, using fresh markup.
        queueMicrotask(() => { if (this.isConnected) { if (this.sidesEnabled) this.nativeSideGroup()?.setAttribute('hidden',''); this.updateExtras(); } });
      };
      this.onPricePending ||= () => { this.variantPending = true; };
      this.priceScope?.addEventListener('variant:selected', this.onPricePending);
      this.priceScope?.addEventListener('variant:update', this.onPriceVariant);
    }
    observePreview() {
      if (!this.preview) return;
      this.onPreviewResize ||= () => this.schedulePreviewFit();
      if (typeof ResizeObserver !== 'undefined') {
        this.previewObserver ||= new ResizeObserver(this.onPreviewResize);
        this.previewObserver.observe(this.preview);
      } else {
        window.addEventListener('resize', this.onPreviewResize);
      }
    }
    cancelPreviewFit() {
      if (this.previewFrame == null) return;
      if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this.previewFrame);
      else clearTimeout(this.previewFrame);
      this.previewFrame = null;
    }
    schedulePreviewFit() {
      if (this.previewFrame != null) return;
      const fit = () => { this.previewFrame = null; if (this.isConnected) this.fitPreview(); };
      this.previewFrame = typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame(fit) : setTimeout(fit, 0);
    }
    showPreviewSide() {
      const sides = Object.keys(this.previewValues || {});
      if (!sides.includes(this.activePreviewSide)) this.activePreviewSide = sides[0];
      this.previewControls.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.previewSide === this.activePreviewSide)));
      const lines = [...(this.previewValues?.[this.activePreviewSide] || [])];
      while (lines.length && !lines.at(-1).trim()) lines.pop();
      const content = lines.some(line => line.trim()) ? lines.join('\n') : '';
      for (const text of this.preview.querySelectorAll('.med-engraving-text')) {
        const next = content || this.exampleLines.join('\n');
        if (text.textContent !== next) text.textContent = next;
        if (text.dataset.placeholder !== String(!content)) text.dataset.placeholder = String(!content);
      }
      this.schedulePreviewFit();
    }
    fitPreview() {
      this.previewFitCache ||= new WeakMap();
      const jobs = [];
      for (const face of this.preview.querySelectorAll('.med-engraving-face')) {
        const text = face.querySelector('.med-engraving-text');
        const faceWidth = face.clientWidth, faceHeight = face.clientHeight;
        if (!text || !faceWidth || !faceHeight) continue;
        const key = `${faceWidth}:${faceHeight}:${text.textContent}`;
        if (this.previewFitCache.get(text) === key) continue;
        jobs.push({ text, key, width: Math.max(1, (faceWidth - 6) / 1.05), height: Math.max(1, faceHeight - 6), low: 0, high: 22, size: 22 });
      }
      for (const job of jobs) job.text.style.fontSize = '22px';
      let pending = jobs.filter(job => {
        if (job.text.scrollWidth <= job.width && job.text.scrollHeight <= job.height) {
          job.low = 22;
          return false;
        }
        return true;
      });
      for (let attempt = 0; attempt < 8 && pending.length; attempt++) {
        for (const job of pending) {
          job.size = (job.low + job.high) / 2;
          job.text.style.fontSize = `${job.size}px`;
        }
        for (const job of pending) {
          if (job.text.scrollWidth <= job.width && job.text.scrollHeight <= job.height) job.low = job.size;
          else job.high = job.size;
        }
      }
      for (const job of jobs) {
        job.text.style.fontSize = `${Math.max(.001, Math.floor(job.low * 1000) / 1000)}px`;
        this.previewFitCache.set(job.text, job.key);
      }
    }
    addGroup(name, count, values, sideKey = name.toLowerCase()) {
      const group = node('fieldset', 'med-engraving-group');
      group.dataset.side = sideKey;
      group.append(node('legend', '', name));
      this.groups.append(group);
      for (let i = 0; i < count; i++) this.addField(group, sideKey, values[i] || '', i + 1);
      if (this.restoredBracelet) return;
      const add = node('button', 'med-add-line', '+ Add a line');
      add.type = 'button';
      add.setAttribute('aria-label', `Add a ${name.toLowerCase()} engraving line`);
      add.addEventListener('click', () => {
        const field = this.addField(group, sideKey, '', group.querySelectorAll('.med-line-input').length + 1);
        this.activePreviewSide = sideKey;
        this.sync(true);
        field.focus();
      });
      group.append(add);
    }
    addField(group, side, value, number) {
      const row = node('div', 'med-line-row');
      const id = `${this.uid}-${side}-${number}`;
      const label = node('label', 'visually-hidden', `${this.sideLabels[side] || side} engraving, line ${number}`);
      label.htmlFor = id;
      const input = node('input', 'med-line-input');
      input.type = 'text';
      input.id = id;
      input.dataset.side = side.toLowerCase();
      input.value = value;
      input.placeholder = number <= 3 ? `e.g. ${this.exampleLines[number - 1]}` : 'Additional details';
      input.autocomplete = 'off';
      input.spellcheck = false;
      input.setAttribute('form', this.dataset.formId);
      input.maxLength = this.characters;

      const counter = node('small', 'med-line-counter');
      counter.id = id + '-counter';
      input.setAttribute('aria-describedby', counter.id);
      row.append(label, input, counter);
      group.insertBefore(row, group.querySelector('.med-add-line'));
      this.fields.push(input);
      return input;
    }
    sync(edited, textOnly = false, sideChanged = false) {
      if (edited) this.confirm.checked = false;
      const mode = this.mode.querySelector('input:checked')?.value || 'Personalized';
      const blank = mode === 'No engraving';
      if (!textOnly) {
      this.builder.hidden = blank;
      if (this.methodOptions) this.methodOptions.hidden = blank;
      const standard = this.querySelector('.med-method-input[data-method="standard"]');
      if (blank && standard) standard.checked = true;
      this.querySelectorAll('.med-method-input').forEach(input => {
        input.disabled = blank || (input.dataset.method === 'premium' && (input.dataset.available !== 'true' || !/^\d+$/.test(input.dataset.variantId || '')));
      });
      this.dataset.engravingMethod = blank ? 'none' : this.querySelector('.med-method-input:checked')?.dataset.method || 'standard';
      this.syncColorFill();
      this.fontControl?.sync?.(blank);
      this.confirm.required = !blank;
      this.confirm.disabled = blank;
      }
      const activeSides = this.sidesEnabled ? (this.selectedSides === 'both' ? ['front','back'] : [this.selectedSides]) : null;
      if (!textOnly && this.sideChoices) {
        this.querySelectorAll('[data-med-side]').forEach(input => {
          input.disabled = blank || (input.dataset.medSide === 'both' && (input.dataset.available !== 'true' || !/^\d+$/.test(input.dataset.variantId || '')));
        });
        const sidesProperty = this.querySelector('[data-med-sides-property]');
        const labelsProperty = this.querySelector('[data-med-labels-property]');
        if (sidesProperty) { sidesProperty.value = this.selectedSides; sidesProperty.disabled = blank; }
        if (labelsProperty) { labelsProperty.value = JSON.stringify(this.sideLabels); labelsProperty.disabled = blank; }
      }
      this.groups.querySelectorAll('[data-side]').forEach(group => {
        if (group.tagName === 'FIELDSET') group.hidden = this.sidesEnabled && !activeSides.includes(group.dataset.side);
      });
      this.fields.forEach(field => {
        field.disabled = blank || (this.sidesEnabled && !activeSides.includes(field.dataset.side));
        field.setCustomValidity('');
        const remaining = String(Math.max(0, this.characters - field.value.length));
        if (field.nextElementSibling.textContent !== remaining) {
          field.nextElementSibling.textContent = remaining;
          field.nextElementSibling.setAttribute('aria-label', `${remaining} characters remaining`);
        }
      });
      const allValues = {};
      this.fields.forEach(field => (allValues[field.dataset.side] ||= []).push(field.value));
      const values = Object.fromEntries(Object.entries(allValues).filter(([side]) => !this.sidesEnabled || activeSides.includes(side)));
      const clean = lines => {
        const result = [...lines];
        while (result.length && !result.at(-1).trim()) result.pop();
        return result;
      };
      const raw = this.structured
        ? Object.entries(values).map(([side, lines]) => clean(lines).length ? `${this.sideLabels[side] || side}:\n${clean(lines).join('\n')}` : '').filter(Boolean).join('\n\n')
        : clean(values.engraving).join('\n');
      this.raw.value = blank ? 'No engraving' : raw;
      this.previewValues = values;
      const sides = Object.keys(values);
      if (this.previewControls.dataset.sides !== sides.join(',')) {
        this.previewControls.dataset.sides = sides.join(',');
        this.previewControls.replaceChildren();
        for (const side of sides) {
          const button = node('button', '', this.sideLabels[side] || 'Engraving');
          button.type = 'button';
          button.dataset.previewSide = side;
          this.previewControls.append(button);
        }
      }
      this.previewControls.hidden = sides.length < 2;
      this.showPreviewSide();
      this.dispatchEvent(new CustomEvent('med:engraving-update', { bubbles:true, detail:{typing:Boolean(edited && textOnly),sideChanged} }));
      if (!textOnly) this.updateExtras();
      let error = '';
      const enabledFields = this.fields.filter(field => !field.disabled);
      if (!blank && !enabledFields.some(field => field.value.trim())) error = 'Enter your engraving or select No engraving.';
      if (!blank && this.sidesEnabled) {
        const empty = activeSides.find(side => !(values[side] || []).some(line => line.trim()));
        if (empty) error = `Enter details for ${this.sideLabels[empty].toLowerCase()}, or choose one engraving side.`;
        if (this.selectedSides === 'both' && this.querySelector('[data-med-side=both]')?.disabled) error = 'Both-sides engraving is currently unavailable. Choose one side.';
      }
      if (!blank && this.restoredBracelet && Object.entries(values).some(([side, lines]) => clean(lines).length > (side === 'front' ? this.front : side === 'back' ? this.back : 0))) error = 'Use no more than 3 engraving lines on each selected side.';
      if (!blank && enabledFields.some(field => field.value.length > this.characters)) error = `Use no more than ${this.characters} characters on each line. Move extra text to another line.`;
      this.invalidField = enabledFields[0];
      this.invalidField?.setCustomValidity(error);
      if (!error && this.colorFillError) error = this.colorFillError;
      this.fontError = this.fontControl?.validationMessage?.() || '';
      if (!error && this.fontError) error = this.fontError;
      this.validationMessage = error;
      this.error.textContent = '';
      drafts.set(this.draftKey, { ...allValues, selectedSides:this.selectedSides, previewSide:this.activePreviewSide, mode, checked: this.confirm.checked, method: this.dataset.engravingMethod, fillColor: this.querySelector('[data-med-fill-color]:checked:not(:disabled)')?.value, extras: [...this.querySelectorAll('input[type=checkbox][data-med-addon]:checked')].map(input => input.dataset.medAddon), wrist: this.querySelector('.med-wrist-select')?.value, font: this.fontControl?.saved });
      if (drafts.size > 20) drafts.delete(drafts.keys().next().value);
    }
    syncColorFill() {
      const panel = this.querySelector('.med-color-fill');
      if (!panel) return;
      const extra = panel.querySelector('[data-med-addon="color-fill"]');
      const premium = this.querySelector('.med-method-input[data-method="premium"]');
      const eligible = this.dataset.engravingMethod === 'premium' && premium?.checked && !premium.disabled;
      const available = extra.dataset.available === 'true' && /^\d+$/.test(extra.dataset.variantId || '');
      panel.hidden = !eligible;
      extra.disabled = !eligible || !available;
      if (extra.disabled) extra.checked = false;
      const active = extra.checked && !extra.disabled;
      const choices = panel.querySelector('.med-color-fill-choices');
      choices.hidden = !active;
      extra.setAttribute('aria-expanded', String(active));
      panel.querySelectorAll('[data-med-fill-color]').forEach(input => {
        input.disabled = !active;
        input.required = active;
        if (!active) input.checked = false;
      });
      const selected = active ? panel.querySelector('[data-med-fill-color]:checked') : null;
      this.colorFillError = active && !selected ? 'Choose a color for your premium engraving.' : '';
      for (const text of this.querySelectorAll('.med-method-card[data-method="premium"] .med-engraving-text')) {
        if (selected) {
          text.style.color = selected.dataset.medFillColor;
          text.style.textShadow = 'none';
        } else {
          // Restore the existing finish colors, including white on black metal.
          text.style.removeProperty('color');
          text.style.removeProperty('text-shadow');
        }
      }
      const label = panel.querySelector('.med-fill-selection');
      const total = Number(premium?.dataset.price || 0) + Number(extra.dataset.price || 0);
      const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency',currency:this.dataset.currency || 'USD'}).format(total / 100);
      label.textContent = selected ? `${selected.value} · Premium engraving + color fill: ${money} per item` : 'Choose one color.';
    }
    updateAccessoryTone(tone) {
      const preview = this.querySelector('.med-text-preview');
      if (preview) preview.dataset.medTone = tone;
      const cards = this.querySelectorAll('[data-extender-tone]');
      let selected = false;
      for (const card of cards) {
        const input = card.querySelector('[data-med-addon]');
        selected ||= input.checked;
        input.checked = false;
        card.hidden = card.dataset.extenderTone !== tone;
        input.disabled = card.hidden || input.dataset.available !== 'true' || !/^\d+$/.test(input.dataset.variantId || '');
      }
      if (selected) {
        const matching = [...cards].find(card => !card.hidden)?.querySelector('[data-med-addon]');
        if (matching && !matching.disabled) matching.checked = true;
      }
      const charmTone = tone === 'gold' ? 'gold' : 'silver';
      for (const card of this.querySelectorAll('[data-charm-tone]')) {
        const input = card.querySelector('[data-med-addon]');
        card.hidden = card.dataset.charmTone !== charmTone;
        input.disabled = card.hidden || input.dataset.available !== 'true' || !/^\d+$/.test(input.dataset.variantId || '');
        if (input.disabled) input.checked = false;
      }
      this.updateExtras();
      requestAnimationFrame(() => this.querySelector('med-accessory-carousel')?.update?.());
    }
    selectedExtras() {
      return [...this.querySelectorAll('[data-med-addon]:checked')].filter(input => !input.disabled);
    }
    updateExtras() {
      const selected = this.selectedExtras();
      // Also record intent in native form data, before either purchase path runs.
      // A base-only fallback can then be recognized by the shopping bag guard.
      const expected = this.querySelector('[data-med-expected-extras]');
      if (expected) expected.value = selected.map(input => input.dataset.variantId).join(',');
      const total = selected.reduce((sum, input) => sum + Number(input.dataset.price || 0), 0);
      const prices = [...(this.priceScope?.querySelectorAll('[data-med-price-product]') || [])]
        .filter(price => price.dataset.medPriceProduct === this.dataset.productId);
      const money = (amount, currencyCode = false) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: this.dataset.currency || 'USD', currencyDisplay: currencyCode ? 'code' : 'symbol'
      }).format(amount / 100);
      let base = this.dataset.basePrice === undefined || this.dataset.basePrice === '' ? NaN : Number(this.dataset.basePrice);
      for (const price of prices) {
        const original = price.querySelector('[data-med-base-display]');
        const configured = price.querySelector('[data-med-configured-display]');
        if (!original || !configured) continue;
        const current = price.dataset.medBasePrice === '' ? NaN : Number(price.dataset.medBasePrice);
        if (Number.isFinite(current)) base = current;
        const active = total > 0 && Number.isFinite(current) && !this.priceUnavailable;
        original.hidden = active;
        configured.hidden = !active;
        if (active) {
          const value = configured.querySelector('[data-med-configured-value]');
          const formatted = money(current + total, price.dataset.medCurrencyCode === 'true');
          if (value.textContent !== formatted) value.textContent = formatted;
        }
      }
      const label = this.querySelector('.med-addons-total');
      if (!label) return;
      label.hidden = total === 0 || this.priceUnavailable === true;
      const message = Number.isFinite(base)
        ? `Your total: ${money(base + total)} per medical ID · Includes ${money(total)} in extras`
        : `Selected extras: ${money(total)} per medical ID`;
      if (label.textContent !== message) label.textContent = message;
    }
    prepareCartPayload(formData) {
      this.syncColorFill();
      if (this.colorFillError) throw new Error(this.colorFillError);
      const extras = this.selectedExtras();
      const id = Number(formData.get('id'));
      const quantity = Number(formData.get('quantity') || 1);
      if (!Number.isSafeInteger(id) || id < 1 || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Please choose a valid product and quantity.');
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && (!(value instanceof File) || value.size)) properties[match[1]] = value;
      }
      this.fontControl?.applyProperties?.(properties, extras);
      const blank = this.dataset.engravingMethod === 'none';
      if (this.sidesEnabled && !blank) {
        const sides = this.querySelector('[data-med-side]:checked');
        if (!sides || sides.disabled) throw new Error('Please choose an available engraving side.');
        if ((sides.dataset.medSide === 'both') !== extras.some(input => input.dataset.medAddon === 'both-sides')) throw new Error('Please review your engraving-side upgrade.');
        properties['Engraving sides'] = sides.value;
        properties._med_engraving_sides = this.selectedSides;
        properties._med_engraving_labels = JSON.stringify(this.sideLabels);
      } else if (blank) {
        delete properties['Engraving sides'];
        delete properties._med_engraving_sides;
        delete properties._med_engraving_labels;
      }
      const fill = extras.find(input => input.dataset.medAddon === 'color-fill');
      const fillColor = this.querySelector('[data-med-fill-color]:checked:not(:disabled)')?.value;
      delete properties['Engraving color'];
      if (fill) {
        if (!extras.some(input => input.dataset.medAddon === 'premium') || !fillColor) throw new Error('Color fill requires premium engraving and one selected color.');
        properties['Engraving color'] = fillColor;
      }
      const bundle = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      properties._med_bundle = bundle;
      const parent = {id,quantity,properties};
      const plan = formData.get('selling_plan');
      if (plan) parent.selling_plan = Number(plan);
      const items = [parent];
      const selectedIds = new Set([id]);
      for (const input of extras) {
        const childId = Number(input.dataset.variantId);
        if (!Number.isSafeInteger(childId) || childId < 1 || selectedIds.has(childId) || input.dataset.available !== 'true') throw new Error('One of your extras is unavailable. Please review your choices.');
        selectedIds.add(childId);
        const childProperties = {_med_bundle:bundle,_med_extra:input.dataset.medAddon};
        if (input.dataset.medAddon === 'color-fill') childProperties['Engraving color'] = fillColor;
      if (['font-choice', 'custom-font'].includes(input.dataset.medAddon)) childProperties['Engraving font'] = properties['Engraving font'];
        items.push({id:childId,quantity,parent_id:id,properties:childProperties});
      }
      // Keep an expected-extra manifest for incomplete/legacy bag protection.
      properties._med_expected_extras = items.slice(1).map(item => item.id).join(',');
      properties._med_purchase_version = '67';
      const payload = {items};
      const sections = formData.getAll('sections').filter(Boolean).at(-1);
      if (sections) payload.sections = sections;
      const sectionsUrl = formData.get('sections_url');
      if (sectionsUrl) payload.sections_url = sectionsUrl;
      return payload;
    }
    cartRequest(payload) {
      const hasFile = payload.items.some(item => Object.values(item.properties || {}).some(value => value instanceof File));
      if (!hasFile) return {headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify(payload)};
      const body = new FormData();
      payload.items.forEach((item, index) => {
        for (const [key, value] of Object.entries(item)) {
          if (key === 'properties') {
            for (const [name, property] of Object.entries(value)) body.append(`items[${index}][properties][${name}]`, property);
          } else body.append(`items[${index}][${key}]`, String(value));
        }
      });
      for (const key of ['sections', 'sections_url']) if (payload[key]) body.append(key, payload[key]);
      return {headers: {Accept: 'application/json'}, body};
    }
    async submitCart(form) {
      if (this.cartBusy || !this.validate()) return;
      if (!form.reportValidity()) return;
      if (this.variantPending || this.priceUnavailable) {
        this.error.textContent = 'Please wait for your product selection to finish updating.';
        return;
      }
      const component = form.closest('product-form-component');
      const quantityCheck = component?.querySelector('quantity-selector-component')?.canAddToCart?.();
      if (quantityCheck && !quantityCheck.canAdd) {
        this.error.textContent = 'Please reduce the quantity to the available amount.';
        return;
      }
      const buttons = [...form.querySelectorAll('button[type="submit"], button[data-med-submit]')];
      if (buttons.some(button => button.disabled)) return;
      let submitted = false;
      try {
        const payload = this.prepareCartPayload(new FormData(form));
        this.cartBusy = true;
        this.error.textContent = '';
        form.setAttribute('aria-busy', 'true');
        buttons.forEach(button => { button.disabled = true; });
        submitted = true;
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST', credentials: 'same-origin',
          ...this.cartRequest(payload)
        });
        const added = await response.json();
        if (!response.ok || added.status) {
          submitted = false;
          throw new Error(added.description || added.message || 'Your items could not be added. Please review their availability.');
        }
        // Verify the saved cart, not just the on-page calculated price.
        const check = await fetch(`${this.dataset.cartUrl}.js`, {credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}});
        if (!check.ok) throw new Error('Cart verification failed.');
        const cart = await check.json();
        const bundle = payload.items[0].properties._med_bundle;
        const complete = Array.isArray(cart.items) && payload.items.every(expected =>
          cart.items.some(actual => Number(actual.variant_id || actual.id) === expected.id &&
            actual.properties?._med_bundle === bundle && Number(actual.quantity) === expected.quantity));
        if (!complete) throw new Error('One or more selected extras did not reach your shopping bag.');
        const savedParent = cart.items.find(item => item.properties?._med_bundle === bundle && Number(item.variant_id || item.id) === payload.items[0].id);
        if (this.fontControl?.verifySaved && !this.fontControl.verifySaved(savedParent?.properties)) throw new Error(this.fontControl.messages.upload_failed);
        window.location.assign(this.dataset.cartUrl);
      } catch (failure) {
        this.error.textContent = submitted
          ? 'We could not confirm all your items. Open your shopping bag and check the jewelry and extras before trying again.'
          : failure.message;
        if (submitted) {
          const link = node('a', 'med-check-bag-link', 'Check your shopping bag');
          link.href = this.dataset.cartUrl;
          this.error.append(link);
        }
        this.error.setAttribute('tabindex', '-1');
        this.error.focus();
      } finally {
        this.cartBusy = false;
        form.removeAttribute('aria-busy');
        buttons.forEach(button => { button.disabled = false; });
      }
    }
    validate() {
      this.sync(false);
      const error = this.validationMessage || (this.confirm.required && !this.confirm.checked ? 'Please check your spelling and confirm your engraving details.' : '');
      this.error.textContent = error;
      if (error) {
        const target = this.fontError && this.validationMessage === this.fontError ? (this.fontControl.key === 'custom' ? this.fontControl.upload : this.fontControl.select) : this.colorFillError && this.validationMessage === this.colorFillError ? this.querySelector('[data-med-fill-color]:not(:disabled)') : this.validationMessage ? this.invalidField || this.fields[0] : this.confirm;
        target?.focus();
        target?.reportValidity();
      }
      return !error;
    }
  }
  // Personalized purchases deliberately have no native submit action on click.
  // The same selection that drives the displayed total owns the complete request.
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-med-submit]');
    if (!button || button.disabled) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const form = button.form;
    if (!form) return;
    // Shopify's input[name=id] shadows form.id in browsers; use the actual attribute.
    const personalizer = [...document.querySelectorAll('med-personalizer')]
      .find(editor => editor.dataset.formId === form.getAttribute('id'));
    if (typeof personalizer?.submitCart === 'function') {
      personalizer.submitCart(form);
    } else {
      const error = personalizer?.querySelector('[role="alert"]') || form.querySelector('.product-form-text__error');
      if (error) {
        error.classList.remove('hidden');
        error.textContent = 'Please refresh this page before adding your personalized item.';
        error.setAttribute('role', 'alert');
      }
    }
  }, true);
  // Form-associated fields are outside Horizon's native form. Capture also guards scripted submissions.
  document.addEventListener('submit', event => {
    if (!(event.target instanceof HTMLFormElement)) return;
    for (const personalizer of document.querySelectorAll('med-personalizer')) {
      if (personalizer.dataset.formId === event.target.getAttribute('id')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        personalizer.submitCart(event.target);
        return;
      }
    }
  }, true);
  class MedicalCartEditor extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.hidden = false;
      this.addEventListener('click', event => {
        if (event.target.closest('[data-save-engraving]')) this.save();
      });
      this.addEventListener('input', event => {
        if (event.target.matches('textarea')) this.querySelector('input[type=checkbox]').checked = false;
      });
    }
    async save() {
      if (this.busy) return;
      const field = this.querySelector('textarea');
      const verified = this.querySelector('input[type=checkbox]');
      const button = this.querySelector('[data-save-engraving]');
      const error = this.querySelector('[role=alert]');
      const value = field.value.trim();
      error.textContent = '';
      if (!value) { error.textContent = 'Enter engraving instructions, or No engraving.'; field.focus(); return; }
      const originalProperties = JSON.parse(this.dataset.properties || '{}');
      const purchasedSides = originalProperties._med_engraving_sides;
      const surfaceLabels = (() => { try { return JSON.parse(originalProperties._med_engraving_labels || '{}'); } catch (_) { return {}; } })();
      if (/^no engraving$/i.test(value) && (this.dataset.engravingMethod === 'Premium Laser Engraving' || purchasedSides === 'both' || (originalProperties._med_font_choice && originalProperties._med_font_choice !== 'arial'))) {
        error.textContent = 'To change paid engraving services, remove this medical ID and add it again with your new choices.';
        field.focus(); return;
      }
      const front = integer(this.dataset.frontLines), back = integer(this.dataset.backLines), characters = scopedEngravingCharacters(this);
      const restoredBracelet = restoredBraceletIds.has(String(this.dataset.productId));
      if (!/^no engraving$/i.test(value)) {
        const structured = Boolean(purchasedSides || front || back);
        let side = structured ? '' : 'engraving';
        const groups = { front: [], back: [], engraving: [] };
        let invalid = false;
        for (const line of value.split('\n')) {
          const heading = structured && line.match(/^(.+):\s*$/);
          const headingSide = heading && ['front','back'].find(key => (surfaceLabels[key] || (key === 'front' ? 'Front' : 'Back')).toLowerCase() === heading[1].toLowerCase());
          if (headingSide) { side = headingSide; continue; }
          if (!line.trim() && !side) continue;
          if (!side) { invalid = true; break; }
          groups[side].push(line);
        }
        for (const lines of Object.values(groups)) while (lines.length && !lines.at(-1).trim()) lines.pop();
        if (!Object.values(groups).flat().some(line => line.trim())) invalid = true;
        if (purchasedSides) {
          const expected = purchasedSides === 'both' ? ['front','back'] : [purchasedSides];
          if (expected.some(side => !groups[side]?.some(line => line.trim())) || ['front','back'].some(side => !expected.includes(side) && groups[side].length)) invalid = true;
        }
        const tooManyLines = restoredBracelet && (groups.front.length > front || groups.back.length > back || groups.engraving.length > 0);
        if (invalid || tooManyLines || (!purchasedSides && !front && groups.front.length) || (!purchasedSides && !back && groups.back.length) || Object.values(groups).flat().some(line => line.length > characters)) {
          error.textContent = `Use no more than ${characters} characters per line.${restoredBracelet ? ' Keep to 3 lines per selected side.' : ''} Keep the headings and text for your chosen engraving surfaces. To change sides, remove this item and add it again.`;
          field.focus(); return;
        }
      }
      if (!verified.checked) { error.textContent = 'Please confirm you have checked your engraving details.'; verified.focus(); return; }
      const label = button.textContent;
      try {
        this.busy = true;
        button.disabled = true;
        field.disabled = true;
        verified.disabled = true;
        button.textContent = 'Saving…';
        const properties = { ...JSON.parse(this.dataset.properties || '{}') };
        properties[this.dataset.propertyKey] = value;
        properties['Personalization'] = /^no engraving$/i.test(value) ? 'No engraving' : 'Personalized';
        properties['Engraving checked'] = 'Yes';
        if (properties['Personalization'] === 'No engraving') {
          delete properties['Engraving method'];
          delete properties['Engraving font'];
          delete properties._med_font_choice;
          delete properties['Custom font file'];
          delete properties['Custom font name'];
        }
        else properties['Engraving method'] ||= 'Standard Etched Engraving';
        const response = await fetch(this.dataset.changeUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: this.dataset.key, properties })
        });
        if (!response.ok) throw new Error('The cart could not be updated. Please reload your cart and try again.');
        const cart = await response.json();
        if (!Array.isArray(cart.items)) throw new Error('Please reload your cart to check whether the change was saved.');
        window.location.assign(this.dataset.cartUrl);
      } catch (failure) {
        error.textContent = failure instanceof TypeError ? 'Connection interrupted. Reload your cart to check whether the change was saved.' : failure.message;
      } finally {
        this.busy = false;
        button.disabled = field.disabled = verified.disabled = false;
        button.textContent = label;
      }
    }
  }
  if (!customElements.get('med-personalizer')) customElements.define('med-personalizer', MedicalPersonalizer);
  if (!customElements.get('med-cart-editor')) customElements.define('med-cart-editor', MedicalCartEditor);
})();
